// src/components/InfoTooltip.js
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import Popup from './Popup/Popup';

const InfoTooltip = ({ 
  text, 
  icon, 
  className,
  position = 'bottom',
  maxWidth = 300,
  mobileTitle = 'Information',
  imageSrc = null,
  imageAlt = '',
  children
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [showMobilePopup, setShowMobilePopup] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const iconRef = useRef(null);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Default icon if none provided
  const defaultIcon = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 16V12" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 8H12.01" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const handleIconClick = () => {
    if (isMobile) {
      setShowMobilePopup(true);
    }
  };

  const handleIconMouseEnter = () => {
    if (!isMobile) {
      setShowTooltip(true);
    }
  };

  const handleIconMouseLeave = () => {
    if (!isMobile) {
      setShowTooltip(false);
    }
  };

  // Calculate tooltip position
  const getTooltipStyle = () => {
    const style = {
      width: `${maxWidth}px`, // Use width instead of maxWidth to force the width
      maxWidth: `${maxWidth}px` // Keep maxWidth for safety
    };

    switch (position) {
      case 'top':
        style.bottom = '100%';
        style.left = '50%';
        style.transform = 'translateX(-50%) translateY(-10px)';
        break;
      case 'right':
        style.left = '100%';
        style.top = '50%';
        style.transform = 'translateY(-50%) translateX(10px)';
        break;
      case 'left':
        style.right = '100%';
        style.top = '50%';
        style.transform = 'translateY(-50%) translateX(-10px)';
        break;
      default: // bottom
        style.top = '100%';
        style.left = '50%';
        style.transform = 'translateX(-50%) translateY(10px)';
    }

    return style;
  };

  // Render popup at the document body level using portal
  const renderMobilePopup = () => {
    if (!showMobilePopup || !isMobile) return null;
    
    return ReactDOM.createPortal(
      <Popup 
        isOpen={true}
        onClose={() => setShowMobilePopup(false)}
      >
        <div className="p-6">
          <h3 className="text-lg font-medium mb-4 pr-8">{mobileTitle}</h3>
          {imageSrc ? (
            <img src={imageSrc} alt={imageAlt} className="w-full h-auto rounded-lg" />
          ) : (
            <p className="text-gray-700 text-base">{text}</p>
          )}
        </div>
      </Popup>,
      document.body
    );
  };

  return (
    <>
      <span 
        ref={iconRef}
        className={`inline-flex items-center align-middle cursor-pointer relative ${className}`}
        onClick={handleIconClick}
        onMouseEnter={handleIconMouseEnter}
        onMouseLeave={handleIconMouseLeave}
        aria-label="More information"
      >
        {children || icon || defaultIcon}
        
        {/* Desktop tooltip */}
        {showTooltip && !isMobile && (
          <div 
            className="absolute bg-white text-gray-800 p-3 rounded-lg shadow-lg z-50 text-sm"
            style={getTooltipStyle()}
          >
            {imageSrc ? (
              <img src={imageSrc} alt={imageAlt} className="w-full h-auto rounded-lg" />
            ) : (
              text
            )}
            <div 
              className="tooltip-arrow absolute w-3 h-3 bg-white transform rotate-45"
              style={{
                top: position === 'bottom' ? '-6px' : 'auto',
                bottom: position === 'top' ? '-6px' : 'auto',
                left: (position === 'top' || position === 'bottom') ? '50%' : 'auto',
                right: position === 'left' ? '-6px' : 'auto',
                marginLeft: (position === 'top' || position === 'bottom') ? '-6px' : '0',
                marginTop: '0',
              }}
            />
          </div>
        )}
      </span>

      {/* Render Mobile popup using portal */}
      {renderMobilePopup()}
    </>
  );
};

InfoTooltip.propTypes = {
  text: PropTypes.string,
  icon: PropTypes.node,
  className: PropTypes.string,
  position: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
  maxWidth: PropTypes.number,
  mobileTitle: PropTypes.string,
  imageSrc: PropTypes.string,
  imageAlt: PropTypes.string,
  children: PropTypes.node
};

export default InfoTooltip;